import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';

import type { CollectionSubcategory } from '@/utils/collections/collections.types';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';

import { CategoryCard } from './components/CategoryCard/CategoryCard';

type Props = {
    /**
     * Crop each porthole to fill its circle instead of letterboxing it. On for the LAAC-2371
     * collections, off for the legacy star-wars page -- see the <img> comment in CategoryCard.
     */
    fillTiles?: boolean;
    /** Omitted for the LAAC-2371 design, which has no heading; supplied by the legacy star-wars page. */
    heading?: string;
    subcategories: CollectionSubcategory[];
};

export const RelatedCategories = ({ fillTiles = false, heading, subcategories }: Props) => {
    const isTabletSize = useIsNarrowerThan(mergedCaterwaulAndHammerUIThemes.breakpoints.tabletNarrowWidthNum);

    if (subcategories.length === 0) {
        return null;
    }

    if (isTabletSize) {
        const cards = subcategories.map((subcategory, index) => ({
            card: (
                // The carousel sizes its items to their content, so the 128px lives here. Without
                // it the tile would take its width from the label, which is the bug the design
                // feedback reported.
                <div className="w-[128px]">
                    <CategoryCard
                        fillTile={fillTiles}
                        href={subcategory.href}
                        imageUrl={subcategory.imageUrl}
                        position={index + 1}
                        title={subcategory.title}
                    />
                </div>
            ),
            key: subcategory.title,
        }));

        return (
            <div className="pb-md">
                {/*
                 * 8px instead of the carousel's 16px, so more of the next porthole bleeds past the
                 * right edge and the row reads as scrollable. This branch only renders below 768px,
                 * where the carousel's `mobile` gap applies.
                 */}
                <Carousel
                    cardGap={{ mobile: 'xs' }}
                    cards={cards}
                    title={heading}
                    useOverlayControls
                />
            </div>
        );
    }

    return (
        <div className="pb-md">
            {heading && <H2>{heading}</H2>}

            {/*
             * 128px is a floor, not a fixed width. `auto-fit` + `minmax(128px,1fr)` grows the tiles
             * to fill a wide desktop -- back to roughly the ~190px they were before this ticket --
             * and drops to fewer columns as the row narrows, so six tiles can never overflow the
             * 720px content box at 768px, where the carousel has not taken over yet.
             */}
            <div className="mt-sm grid grid-cols-[repeat(auto-fit,minmax(128px,1fr))] gap-md">
                {subcategories.map(({ href, imageUrl, title }, index) => (
                    <CategoryCard
                        fillTile={fillTiles}
                        href={href}
                        imageUrl={imageUrl}
                        key={title}
                        position={index + 1}
                        title={title}
                    />
                ))}
            </div>
        </div>
    );
};
